import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickReferencePanel = () => {
  const [copiedCode, setCopiedCode] = useState('');

  const quickLinks = [
    { label: 'Getting Started', anchor: '#getting-started', icon: 'Play' },
    { label: 'Authentication', anchor: '#auth-overview', icon: 'Shield' },
    { label: 'Categories API', anchor: '#categories-api', icon: 'FolderTree' },
    { label: 'Products API', anchor: '#products-api', icon: 'Package' },
    { label: 'Orders API', anchor: '#orders-api', icon: 'ShoppingCart' },
    { label: 'Error Codes', anchor: '#best-practices', icon: 'AlertCircle' }
  ];

  const codeExamples = [
    {
      title: 'Basic API Call',
      language: 'curl',
      code: `curl -X 'GET'
  'https://lsorder.lakeetech.com/product_details/all'
  -H 'accept: */*'
  -H 'Authorization: Bearer [TOKEN]'`
    },
    {
      title: 'Create Order',
      language: 'curl',
      code: `curl -X 'POST'
  'https://lsorder.lakeetech.com/order/create'
  -H 'accept: */*'
  -H 'Authorization: Bearer [TOKEN]'
  -H 'Content-Type: application/json'
  -d '[
  {
    "merchant_id": "[MERCHANT_ID]"
    client_order_id": "string"
    order_date": "2023-09-23"
    customer_details": {
      customer_name": "Yuvaraj Murugadas",
      customer_email": "yuvayr@gmail.com",
      customer_mobile": 8667767666,
      alternate_number": 7700899888,
      address_details": {
        ship_to_name": "Yuvaraja Murugadas",
        address_line1": "10/21, Srinivasan Street",
        address_line2": "Raja Annmalai Puram",
        address_line3": "Mandaveli",
        address_line4": "",
        landmark": "St Marry Road",
        city": "Chennai",
        state": "Tamil Nadu",
        pincode": 600004
      }
    },
    "product_details": {
      client_product_code": "HOK-KAP-SDM-0053",
      product_name": "BPL BSMG0052S 750W MX Series Grill Sandwich Maker",
      quantity": 1
    },
    "additional_info": {
      program_name": "payback rewards",
      po_number": "PO768768ST",
      invoice_price": "5000",
      redemption_details": {
        redemption_type": "test",
        points": 0,
        amount": 0
      }
    }
  }
]`
    },
    {
      title: 'Get Order Status',
      language: 'curl',
      code: `curl -X 'GET' 
'https://lsorder.lakeetech.com/order/id/SAMPLE-LS-1-34903016' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`
    }
  ];

  const copyToClipboard = async (code, title) => {
    try {
      await navigator.clipboard?.writeText(code);
      setCopiedCode(title);
      setTimeout(() => setCopiedCode(''), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleQuickLinkClick = (anchor) => {
    const element = document.querySelector(anchor);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-card border-l border-border h-full overflow-y-auto">
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Quick Reference</h3>
        
        {/* Quick Links */}
        <div className="mb-6">
          <h4 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
            Quick Links
          </h4>
          <div className="space-y-2">
            {quickLinks?.map((link) => (
              <button
                key={link?.anchor}
                onClick={() => handleQuickLinkClick(link?.anchor)}
                className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name={link?.icon} size={14} />
                <span>{link?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Code Examples */}
        <div className="mb-6">
          <h4 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
            Code Examples
          </h4>
          <div className="space-y-4">
            {codeExamples?.map((example) => (
              <div key={example?.title} className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-xs font-medium text-foreground">{example?.title}</h5>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName={copiedCode === example?.title ? "Check" : "Copy"}
                    onClick={() => copyToClipboard(example?.code, example?.title)}
                    className="h-6 px-2"
                  />
                </div>
                <pre className="text-xs text-muted-foreground overflow-x-auto">
                  <code>{example?.code}</code>
                </pre>
              </div>
            ))}
          </div>
        </div>

        {/* API Status */}
        <div className="mb-6">
          <h4 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
            API Status
          </h4>
          <div className="bg-success/10 border border-success/20 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-sm font-medium text-success">All Systems Operational</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Last updated: {new Date()?.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Support Links */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
            Support
          </h4>
          <div className="space-y-2">
            <a
              href="mailto:api-support@lakeee.shop"
              className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="Mail" size={14} />
              <span>Email Support</span>
            </a>
            {/* <a
              href="#"
              className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="MessageCircle" size={14} />
              <span>Live Chat</span>
            </a>
            <a
              href="#"
              className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="ExternalLink" size={14} />
              <span>Status Page</span>
            </a> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickReferencePanel;